import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ImageCarousel from '../components/ImageCarousel';
import './Home.css';

const Home: React.FC = () => {
  const carouselSlides = [
    {
      src: '/images/banner/vid.mp4',
      alt: 'Gener8ions Church welcome video',
      type: 'video' as const,
      poster: '/images/banner/vid_poster.jpg',
      loopCount: 'infinite' as const,
    },
    {
      src: '/images/banner/pod_cast_sunday.jpg',
      alt: 'Podcast Sunday at Gener8ions Church',
      type: 'image' as const,
      // loopCount defaults to 1 (5 seconds), then advances
    },
    {
      src: '/images/banner/words_series.jpg',
      alt: 'Words - Gener8ions Church message series with scripture on the power of our words',
      type: 'image' as const,
    },
    {
      src: '/images/banner/next_steps.jpg',
      alt: 'Next Steps at Gener8ions Church - Let us help you find your next step',
      type: 'image' as const,
    },
    {
      src: '/images/banner/intercessory_prayer.jpg',
      alt: 'Intercessory Prayer with Apostle Brian - Mondays and Fridays at 5AM via GoTo',
      type: 'image' as const,
    },
    {
      src: '/images/banner/the_huddle.jpg',
      alt: 'The Huddle - Spiritual formation for men, devotion time with Apostle Brian every Saturday night at 8PM',
      type: 'image' as const,
    },
    {
      src: '/images/banner/1000_miles_of_hope.jpg',
      alt: '1000 Miles of Hope Challenge - Breast Cancer Awareness Month initiative by The Table with Dr. K',
      type: 'image' as const,
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-carousel">
          <ImageCarousel slides={carouselSlides} />
        </div>
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1>Find Your Place of Belonging</h1>
          <p className="hero-tagline">Trust | Love | Understand</p>
          <p className="hero-description">
            At Gener8ions Church, you'll discover a community where everyone belongs. 
            We're a vibrant, multicultural family dedicated to growing in faith together 
            through authentic worship, practical Bible teaching, and genuine relationships.
          </p>
          <p className="hero-sub-description">
            <strong>No judgment. No condemnation.</strong> Just unconditional love and grace.
          </p>
          <div className="hero-buttons">
            <Link to="/services" className="btn btn-primary">Plan Your Visit</Link>
            <Link to="/sermons" className="btn btn-secondary">Watch Online</Link>
          </div>
        </motion.div>
      </section>

      {/* Why Visit Section */}
      <section className="why-visit-section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Why Visit Gener8ions?</motion.h2>
            <motion.p className="section-intro" variants={fadeInUp}>
              Discover what makes our church a place where lives are transformed and hearts find home.
            </motion.p>
            <div className="why-visit-grid">
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">💝</div>
                <h3>You'll Feel Welcome</h3>
                <p>
                  From the moment you walk through our doors, you're family. We're committed to 
                  creating a healthy, positive atmosphere where you can be yourself—no pretense, 
                  no expectations, just genuine acceptance.
                </p>
              </motion.div>
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">📚</div>
                <h3>Practical Bible Teaching</h3>
                <p>
                  Our messages aren't just inspirational—they're transformational. We teach 
                  God's Word in a way that applies to your everyday life, helping you grow 
                  spiritually and live out your faith authentically.
                </p>
              </motion.div>
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">🤝</div>
                <h3>Real Community</h3>
                <p>
                  Life is better together. Join us for meaningful connections, mentorship 
                  opportunities, and authentic relationships that support you through every 
                  season of life.
                </p>
              </motion.div>
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">🌍</div>
                <h3>Multicultural Community</h3>
                <p>
                  Our multicultural community reflects the beauty of God's creation. 
                  Every background, every generation, every story matters here. 
                  You'll find unity in our shared faith.
                </p>
              </motion.div>
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">🙏</div>
                <h3>Authentic Worship</h3>
                <p>
                  Experience the presence of God through heartfelt worship and prayer. 
                  Whether you're a longtime believer or exploring faith for the first time, 
                  you'll find space to connect with God.
                </p>
              </motion.div>
              <motion.div className="why-card" variants={fadeInUp}>
                <div className="why-icon">⭐</div>
                <h3>Life-Giving Culture</h3>
                <p>
                  We believe in investing in people—mentoring, discipleship, and helping 
                  you develop a personal relationship with God. Your spiritual growth is 
                  our priority.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="expect-section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>What to Expect</motion.h2>
            <motion.p className="section-intro" variants={fadeInUp}>
              Visiting a new church can feel overwhelming. We're here to make it easy.
            </motion.p>
            <div className="expect-grid">
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">01</div>
                <h3>Casual & Comfortable</h3>
                <p>
                  Come as you are. There's no dress code. Whether you prefer jeans or your 
                  Sunday best, you'll fit right in. Comfort is what matters.
                </p>
              </motion.div>
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">02</div>
                <h3>Warm Welcome</h3>
                <p>
                  Our greeters will welcome you with a smile and help you find your way. 
                  No pressure, no awkward moments—just genuine hospitality.
                </p>
              </motion.div>
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">03</div>
                <h3>Meaningful Worship</h3>
                <p>
                  Our service starts with passionate worship followed by practical, 
                  life-changing teaching from God's Word. Service typically lasts about 90 minutes.
                </p>
              </motion.div>
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">04</div>
                <h3>No Pressure</h3>
                <p>
                  You won't be singled out or asked to stand. We won't ask for money. 
                  Simply come, experience, and see if Gener8ions feels like home to you.
                </p>
              </motion.div>
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">05</div>
                <h3>Family-Friendly</h3>
                <p>
                  Children of all ages are welcome in our services. We value families 
                  worshiping together and provide a safe, welcoming environment for everyone.
                </p>
              </motion.div>
              <motion.div className="expect-card" variants={fadeInUp}>
                <div className="expect-number">06</div>
                <h3>Easy to Find</h3>
                <p>
                  Located at 9644 Maumelle Blvd, North Little Rock, AR 72113, with plenty of parking.
                  Can't make it in person? Join us online for live or recorded services.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Our Mission</motion.h2>
            <motion.p className="mission-text" variants={fadeInUp}>
              To build relationships of trust, share the love of God, and understand generations, 
              that all may be enriched and developed as disciples of Christ.
            </motion.p>
            <motion.div className="scripture-container" variants={fadeInUp}>
              <p className="scripture">
                "But ye are a chosen generations, a royal priesthood, an holy nation, a peculiar people; 
                that ye should shew forth the praises of him who hath called you out of darkness into his marvelous light."
              </p>
              <p className="scripture-reference">— I Peter 2:9</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Service Times CTA */}
      <section className="services-cta">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Join Us This Week</motion.h2>
            <motion.div className="services-grid" variants={staggerContainer}>
              <motion.div className="service-time-card" variants={fadeInUp}>
                <h3>Sunday</h3>
                <div className="service-item">
                  <strong>Corporate Prayer</strong>
                  <p>8:00 AM</p>
                  <span className="service-note">Start your week in prayer</span>
                </div>
                <div className="service-item">
                  <strong>Worship & The Word</strong>
                  <p>9:00 AM</p>
                  <span className="service-note">Main service for the whole family</span>
                </div>
              </motion.div>
              <motion.div className="service-time-card" variants={fadeInUp}>
                <h3>Wednesday</h3>
                <div className="service-item">
                  <strong>Corporate Prayer</strong>
                  <p>6:00 PM</p>
                  <span className="service-note">Mid-week spiritual refreshment</span>
                </div>
                <div className="service-item">
                  <strong>Journey Life Application Bible Study (JLABS)</strong>
                  <p>7:00 PM - 8:15 PM</p>
                  <span className="service-note">Interactive study for practical living</span>
                </div>
              </motion.div>
            </motion.div>
            <motion.div variants={fadeInUp} style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/services" className="btn btn-outline">View Full Schedule & Location</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="contact-cta">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp}>Ready to Take the Next Step?</motion.h2>
            <motion.p variants={fadeInUp}>
              Have questions? Want to learn more? We're here to help you on your journey.
            </motion.p>
            <motion.div className="cta-buttons" variants={fadeInUp}>
              <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
              <Link to="/about" className="btn btn-secondary">Learn More About Us</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;

